import { useEffect, useState } from "react";
import api from "../../api/api";
import { environment } from "../../environments/environment";
import { toast } from "react-toastify";
import { Field, Form, Formik, FormikHelpers } from "formik";
import { Skeleton, TextField } from "@mui/material";
import { BlackButton } from "../../components/black-button";
import { countries, states } from "../../constants";
import { FaShoppingCart } from "react-icons/fa";
import { DashboardTitle } from "../../components/DashboardTitle";
import { useSelector } from "react-redux";
import { IAppState } from "../../store/store";
import { currencyFormat, showCurrency } from "../../utility/currencyView";
import { useNavigate } from "react-router-dom";


const initialValues = {
  first_name: "",
  last_name: "",
  middle_name: "",
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  zip: "",
  country: "Ghana",
  phone: "",
  email: "",
};


interface CartItem {
  amount: number;
  campaign_title: string;
  campaign_uuid: string;
  cart_item_uuid: string;
  image_url: string;
  quantity: number;
  size: string;
  title: string;
  unit_price_ghs: number;
  unit_price_ngn: number;
  unit_price_zar: number;
}

interface CartTotal {
  total_amount_ghs: number;
  total_amount_ngn: number;
  total_amount_zar: number;
}

interface ApiCartItem {
      amount_ngn?: number;
      product_title?: string;
      product_uuid?: string;
      cart_item_uuid?: string;
      image_url?: string;
      quantity?: number;
      size?: string;
      title?: string;
      unit_price_ghs?: number | string;
      unit_price_ngn?: number | string;
      unit_price_zar?: number | string;
    }

export const CartCheckout = () => {
  const navigate = useNavigate()
  const cart_uuid = localStorage.getItem("cart_uuid");
  const [isLoading, setIsLoading] = useState(false);
  const [orderSummary, setOrderSummary] = useState<any>(null);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [cartTotal, setCartTotal] = useState<CartTotal>();
  const [summaryLoading, setSummaryLoading] = useState(true);
   const currencyView = useSelector((state: IAppState) =>
          showCurrency(state.shared.currency)
        );


  useEffect(() => {
  const fetchOrderSummary = async () => {
    if (!cart_uuid) return;
    
    try {
      setSummaryLoading(true);
      const response = await api.get(`${environment.baseUrl}/product/checkout?cart_uuid=${cart_uuid}`);
      
      // Debug what's coming from the API
      console.log("Raw API response data:", response.data.data);
      
      const cartItemsWithValidPrices: CartItem[] = response.data.data.cart_items.map((item: ApiCartItem) => {
        // Debug each item's price values before conversion
        console.log("Before conversion:", {
          unit_price_ghs: item.unit_price_ghs,
          unit_price_ngn: item.unit_price_ngn,
          unit_price_zar: item.unit_price_zar
        });
        
        const convertedItem = {
          ...item,
          amount: Number(item.amount_ngn) || 0,
          campaign_title: item.product_title || "",
          campaign_uuid: item.product_uuid || "",
          cart_item_uuid: item.cart_item_uuid || "",
          image_url: item.image_url || "",
          quantity: Number(item.quantity) || 0,
          size: item.size || "",
          title: item.title || "",
          unit_price_ghs: parseFloat(String(item.unit_price_ghs)) || 0,
          unit_price_ngn: parseFloat(String(item.unit_price_ngn)) || 0,
          unit_price_zar: parseFloat(String(item.unit_price_zar)) || 0
        };
        
        // Debug after conversion
        console.log("After conversion:", {
          unit_price_ghs: convertedItem.unit_price_ghs,
          unit_price_ngn: convertedItem.unit_price_ngn,
          unit_price_zar: convertedItem.unit_price_zar
        });
        
        return convertedItem;
      });
      
      setCartItems(cartItemsWithValidPrices);
      
      // Debug total values
      console.log("Total values before conversion:", {
        total_ghs: response.data.data.total_amount_ghs,
        total_ngn: response.data.data.total_amount_ngn,
        total_zar: response.data.data.total_amount_zar
      });
      
      setCartTotal({
        total_amount_ngn: parseFloat(String(response.data.data.total_amount_ngn)) || 0,
        total_amount_ghs: parseFloat(String(response.data.data.total_amount_ghs)) || 0,
        total_amount_zar: parseFloat(String(response.data.data.total_amount_zar)) || 0
      });
      
      setOrderSummary(response.data.data);
    } catch (error) {
      console.error("Error fetching order summary:", error);
      toast.error("Could not retrieve your order details");
    } finally {
      setSummaryLoading(false);
    }
  };

  fetchOrderSummary();
}, [cart_uuid]);

  const handleSubmit = async (
        values: any,
        { setSubmitting }: FormikHelpers<any>
  ) => {

        // console.log(values);
        setSubmitting(false);
  
        const orderPayload = {
          ...values,
          cart_uuid: cart_uuid,
    };
    
  
        try {
          setIsLoading(true)
          const response = await api.post(
            `${environment.baseUrl}/product/order/buy-yourself`, orderPayload)
  
          // console.log(response.data.data);
          const checkoutLink = response.data.data.payment_link;
          // navigate(checkoutLink);
          // Open the checkout link in a new tab
          window.open(checkoutLink, '_blank');
  
          localStorage.removeItem("cart_uuid");
        } catch (error) {
          console.error(error)
        } finally {
          setIsLoading(false)
        }
      }

  // Check if user is logged in
  const isLoggedIn = useSelector((state: IAppState) => state.auth.token);

  useEffect(() => {
    // If user is not logged in, redirect to login page
    if (!isLoggedIn) {
      // toast.error("Please log in to continue with checkout");
      navigate("/login");
    }
  }, [isLoggedIn, navigate]);


  return (<section className={`${cart_uuid ? "web_padding_x web_padding_y" : ""} !pt-5`}>
      {
        isLoading && (
          <div className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-40 backdrop-blur-sm z-50 flex items-center justify-center transition-all duration-300">
            <div className="bg-white p-8 rounded-2xl shadow-2xl border border-gray-100 w-80 flex flex-col items-center">
              <div className="bg-gray-50 p-4 rounded-full mb-4 relative">
                <FaShoppingCart className="text-4xl text-black opacity-20" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="h-12 w-12 rounded-full border-4 border-t-black border-r-gray-300 border-b-gray-300 border-l-gray-300 animate-spin"></div>
                </div>
              </div>
              <h3 className="font-bold text-xl mb-1">Processing Order</h3>
              <p className="text-gray-600 text-center">Please wait while we prepare your order...</p>
              <div className="w-full mt-4 bg-gray-100 h-1.5 rounded-full overflow-hidden">
                <div className="h-full bg-black animate-pulse rounded-full"></div>
              </div>
            </div>
          </div>
        )
      }
          <DashboardTitle
            text={"Order Details"}
          ></DashboardTitle>
    
          <div className="grid md:grid-cols-12 gap-8 mt-8">
        {/* Form Section */}
        <div className="md:col-span-8">
          <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
          >
            {({ isValid, isSubmitting }) => (
              <Form>
                <div className="dashboard_subtitle md:mt-0">
                  Your Order Information
                </div>
    
                {/* ...existing form fields... */}
                <div className="grid gap-5 mt-4 md:grid-cols-2 xl:grid-cols-3">
                  <div className="">
                    <h6 className="font-bold text-sm md:text-base">First Name</h6>
                    <Field
                      name="first_name"
                      as={TextField}
                      className="admin_input_1 block_input"
                      placeholder="First Name...."
                    />
                  </div>
                  <div className="">
                    <h6 className="font-bold text-sm md:text-base">Middle Name <small> (optional)</small> </h6>
                    <Field
                      name="middle_name"
                      as={TextField}
                      className="admin_input_1 block_input"
                      placeholder="Middle Name...."
                    />
                  </div>
                  <div className="">
                    <h6 className="font-bold text-sm md:text-base">Last Name </h6>
                    <Field
                      name="last_name"
                      as={TextField}
                      className="admin_input_1 block_input"
                      placeholder="Last Name...."
                    />
                  </div>
                </div>
    
    
                <div className="grid gap-5 mt-4 md:grid-cols-2 xl:grid-cols-3">
                <div className="">
                    <h6 className="font-bold text-sm md:text-base">Phone </h6>
                    <Field
                      name="phone"
                      as={TextField}
                      className="admin_input_1 block_input"
                      placeholder="Phone Number...."
                    />
                  </div>
                <div className="">
                    <h6 className="font-bold text-sm md:text-base">Email </h6>
                    <Field
                      name="email"
                      as={TextField}
                      className="admin_input_1 block_input"
                      placeholder="Email...."
                    />
                  </div>
                  <div className="">
                    <h6 className="font-bold text-sm md:text-base">Address Line 1</h6>
                    <Field
                      name="address_line1"
                      as={TextField}
                      className="admin_input_1 block_input"
                      placeholder="Street address"
                    />
                  </div>
                  <div className="">
                    <h6 className="font-bold text-sm md:text-base">Address Line 2</h6>
                    <Field
                      name="address_line2"
                      as={TextField}
                      className="admin_input_1 block_input"
                      placeholder="(optional)"
                    />
                  </div>
                  <div className="">
                    <h6 className="font-bold text-sm md:text-base">City</h6>
                    <Field
                      name="city"
                      as={TextField}
                      className="admin_input_1 block_input"
                      placeholder="City"
                    />
                  </div>
                  <div className="">
                    <h6 className="font-bold text-sm md:text-base">Country</h6>
                    <Field
                      name="country"
                      as="select"
                      className="rounded-full py-2 w-full border mt-3 pl-3"
                      // classNamePrefix="custom_select"
                    >
                      {countries.map((country, index )=> (
                        <option key={index} value={country}>{country}</option>
                      ))}
                    </Field>
                  </div>
                  {/* <div className="">
                    <h6 className="font-bold text-sm md:text-base">State</h6>
                    <Field
                      name="state"
                      as="select"
                      className="rounded-full py-2 w-full border mt-3 pl-3"
                      // classNamePrefix="custom_select"
                    >
                      <option value="">Select State</option>
                      {states.map((state, index) => (
                        <option key={index} value={state}>{state}</option>
                      ))}
                    </Field>
                  </div> */}
                  <div className="">
                    <h6 className="font-bold text-sm md:text-base">ZIP/Postal Code</h6>
                    <Field
                      name="zip"
                      as={TextField}
                      className="admin_input_1 block_input"
                      placeholder="ZIP Code"
                    />
                  </div>
                  
                  
                </div>
    
                <div className="mt-5 w-full max-w-[300px] flex flex-col">
                  <BlackButton
                    text="Proceed to Payment"
                    type="submit"
                    isDisabled={!isValid}
                    isLoading={isSubmitting}
                  ></BlackButton>
                </div>
              </Form>
            )}
          </Formik>
        </div>
        
        {/* Order Summary Section */}
        <div className="md:col-span-4">
          <div className="bg-gray-50 rounded-xl p-5 sticky top-5">
            <h2 className="text-lg font-bold mb-4">Order Summary</h2>
            
            {summaryLoading ? (
              <div className="space-y-3">
                <Skeleton variant="rectangular" height={24} />
                <Skeleton variant="rectangular" height={24} />
                <Skeleton variant="rectangular" height={24} />
                <Skeleton variant="rectangular" height={40} />
              </div>
            ) : orderSummary ? (
              <div>
                {/* Items List */}
                <div className="space-y-3 mb-4 max-h-[300px] overflow-y-auto">
                 
                    {cartItems.map((item, index) => (
                      <div key={index} className="flex justify-between items-center py-2 border-b border-gray-200">
                        <div className="flex items-center space-x-3">
                          {item.image_url && (
                            <img 
                              src={item.image_url} 
                              alt={item.title} 
                              className="w-12 h-12 object-cover rounded"
                            />
                          )}
                          <div>
                            <p className="font-medium">{item.title}</p>
                            <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                          </div>
                        </div>
                        <p className="font-medium">{currencyView} 
                          {currencyView === "GH₵" ? currencyFormat(item.unit_price_ghs): currencyView === "NGN" ? currencyFormat(item.unit_price_ngn):currencyView === "ZAR" ? currencyFormat(item.unit_price_zar): 0}
                        </p>
                      </div>
                    ))}
                </div>
                
                {/* Totals */}
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <p className="font-medium text-lg">Subtotal</p>
                  <p className="font-bold text-lg">
  {currencyView}
  {currencyView === "GH₵"
    ? currencyFormat(cartTotal?.total_amount_ghs!)
    : currencyView === "NGN"
    ? currencyFormat(cartTotal?.total_amount_ngn!)
    : currencyView === "ZAR"
    ? currencyFormat(cartTotal?.total_amount_zar!)
    : 0}
</p>

                    </div>
                  
              </div>
            ) : (
              <div className="text-center py-4 text-gray-500">
                No items in your cart
              </div>
            )}
          </div>
        </div>
      </div>
        </section>)
}